
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { APP_VERSION } from '../utils/version';
import { formatErrorMessage, showErrorMessage } from '../utils/errorHandler';
import NoTranslate from '../components/NoTranslate';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: '',
    is_active: '',
    search: ''
  });

  const [employeeSearchInput, setEmployeeSearchInput] = useState('');
  const userListFetchCompletedRef = useRef(false);

  // Selection feature
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(true);
  
  // User creation/editing modal
  const [userModal, setUserModal] = useState({
    show: false,
    mode: 'create', // 'create' or 'edit'
    user: {
      id: null,
      employee_id: '',
      full_name: '',
      email: '',
      department: '',
      position: '',
      hire_date: '',
      annual_leave_days: 20,
      slack_user_id: '',
      is_admin: false,
      is_approver: false,
      is_active: true
    }
  });

  // CSV import
  const [csvModal, setCsvModal] = useState({
    show: false,
    file: null,
    importing: false
  });

  // Password reset result
  const [resetResult, setResetResult] = useState(null);

  // Display toggle feature
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      is_active: showAllUsers ? '' : 'true'
    }));
  }, [showAllUsers]);

  useEffect(() => {
    const id = setTimeout(() => {
      const q = employeeSearchInput.trim();
      setFilters(prev => (prev.search === q ? prev : { ...prev, search: q }));
    }, 300);
    return () => clearTimeout(id);
  }, [employeeSearchInput]);

  const fetchUsers = useCallback(async () => {
    try {
      if (!userListFetchCompletedRef.current) {
        setLoading(true);
      }
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/users?${params}`);
      setUsers(response.data);
    } catch (error) {
      console.error('User list fetch error:', error);
      toast.error('Failed to fetch user list');
    } finally {
      setLoading(false);
      userListFetchCompletedRef.current = true;
    }
  }, [filters]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await api.get('/users/departments/list');
      setDepartments(response.data);
    } catch (error) {
      console.error('Department list fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, [filters, fetchUsers, fetchDepartments]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Selection feature
  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const activeUserIds = users.filter(user => Boolean(user.is_active)).map(user => user.id);
      setSelectedUsers(activeUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  // Bulk deactivate (selected rows are always active users)
  const handleBulkDeactivate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to deactivate');
      return;
    }

    if (!window.confirm(`Deactivate ${selectedUsers.length} selected user(s)?`)) {
      return;
    }

    try {
      await Promise.all(selectedUsers.map(userId =>
        api.patch(`/users/${userId}/deactivate`)
      ));
      toast.success(`${selectedUsers.length} user(s) deactivated`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error('Bulk deactivate error:', error);
      toast.error('Bulk deactivate failed');
    }
  };

  const handleDeactivateUser = async (userId, employeeId) => {
    if (!window.confirm(`Deactivate ${employeeId}?`)) {
      return;
    }

    try {
      await api.patch(`/users/${userId}/deactivate`);
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      console.error('User deactivate error:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const handleActivateUser = async (userId, employeeId) => {
    if (!window.confirm(`Activate ${employeeId}?`)) {
      return;
    }

    try {
      await api.patch(`/users/${userId}/activate`);
      toast.success('User activated');
      fetchUsers();
    } catch (error) {
      console.error('User activate error:', error);
      toast.error('Failed to activate user');
    }
  };

  const openUserModal = (mode, user = null) => {
    setUserModal({
      show: true,
      mode,
      user: user ? {
        ...user
      } : {
        id: null,
        employee_id: '',
        full_name: '',
        email: '',
        department: '',
        position: '',
        annual_leave_days: 20,
        slack_user_id: '',
        is_admin: false,
        is_approver: false,
        is_active: true
      }
    });
  };

  const closeUserModal = () => {
    setUserModal({ ...userModal, show: false });
  };

  const handleUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserModal(prev => ({
      ...prev,
      user: {
        ...prev.user,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const alRaw = parseFloat(userModal.user.annual_leave_days);
      const annualLeaveDays = Number.isNaN(alRaw) ? 20 : Math.round(alRaw * 2) / 2;
      const userData = {
        ...userModal.user,
        annual_leave_days: annualLeaveDays
      };

      let response;
      if (userModal.mode === 'create') {
        response = await api.post('/users', userData);
        toast.success(`User created. Initial password: ${response.data.initial_password}`);
      } else {
        await api.put(`/users/${userModal.user.id}`, userData);
        toast.success('User information updated');
      }

      closeUserModal();
      fetchUsers();
    } catch (error) {
      console.error('User operation error:', error);
      const message = formatErrorMessage(error);
      showErrorMessage(toast.error, message);
    }
  };

  const handlePasswordReset = async (userId, employeeId) => {
    if (!window.confirm(`Reset password for ${employeeId}?`)) {
      return;
    }

    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      setResetResult({
        employeeId,
        newPassword: response.data.new_password
      });
      toast.success('Password reset successfully');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Password reset failed');
    }
  };

  const handleCsvFileChange = (e) => {
    setCsvModal(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const handleCsvImport = async () => {
    if (!csvModal.file) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setCsvModal(prev => ({ ...prev, importing: true }));

      const formData = new FormData();
      formData.append('csv', csvModal.file);

      const response = await api.post('/users/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      
      // Display result details
      if (response.data.errors.length > 0) {
        console.log('Import errors:', response.data.errors);
        toast.warning(`${response.data.error_count} errors occurred. Check console for details.`);
      }

      setCsvModal({ show: false, file: null, importing: false });
      fetchUsers();
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('CSV import failed');
      setCsvModal(prev => ({ ...prev, importing: false }));
    }
  };

  const downloadCsvTemplate = () => {
    const headers = [
      'employee_id', 'full_name', 'email', 'department', 
      'position', 'hire_date', 'annual_leave_days', 'is_admin', 'is_approver', 'slack_user_id', 'is_active', 'initial_password'
    ];
    
    const sampleData = [
      'EMP999', 'John Doe', 'john@company.com', 'Sales',
      'Sales Rep', '2024-01-01', '20', 'false', 'false', 'U123456789', 'true', 'EMP999123'
    ];

    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'users_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>User Management</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
        <div className="admin-actions">
          <button 
            className={`btn ${showAllUsers ? 'btn-warning' : 'btn-info'}`}
            onClick={() => setShowAllUsers(!showAllUsers)}
          >
            {showAllUsers ? 'Show Active Only' : 'Show All Users'}
          </button>
          {selectedUsers.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={handleBulkDeactivate}
            >
              Deactivate Selected ({selectedUsers.length})
            </button>
          )}
          <button 
            className="btn btn-secondary" 
            onClick={() => setCsvModal({ show: true, file: null, importing: false })}
          >
            CSV Import
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => openUserModal('create')}
          >
            Create New User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <h3>Filters</h3>
        <div className="grid grid-3">
          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              name="department"
              className="form-control notranslate"
              translate="no"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              name="is_active"
              className="form-control"
              value={filters.is_active}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="user-management-employee-search">
              Employee search
            </label>
            <input
              id="user-management-employee-search"
              type="search"
              className="form-control"
              placeholder="Employee ID, Name, or Email"
              value={employeeSearchInput}
              onChange={(e) => setEmployeeSearchInput(e.target.value)}
              autoComplete="off"
            />
            <small className="text-muted d-block mt-1">
              Employee ID, Name, or Email — partial match, any field.
            </small>
            <small className="text-muted d-block">{users.length} users</small>
          </div>
        </div>
      </div>

      {/* Password reset result display */}
      {resetResult && (
        <div className="card mb-4 alert-success">
          <h4>Password Reset Complete</h4>
          <p><strong>Employee ID:</strong> {resetResult.employeeId}</p>
          <p><strong>New Password:</strong> <code>{resetResult.newPassword}</code></p>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={() => setResetResult(null)}
          >
            Close
          </button>
        </div>
      )}

      {/* User list */}
      <div className="card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={selectedUsers.length > 0 && selectedUsers.length === users.filter(user => Boolean(user.is_active)).length}
                  />
                </th>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Position</th>
                <th>Permissions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className={!user.is_active ? 'inactive-user' : ''}>
                  <td>
                    {Boolean(user.is_active) && (
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                      />
                    )}
                  </td>
                  <td><NoTranslate>{user.employee_id}</NoTranslate></td>
                  <td><NoTranslate>{user.full_name}</NoTranslate></td>
                  <td><NoTranslate>{user.email}</NoTranslate></td>
                  <td><NoTranslate>{user.department || '-'}</NoTranslate></td>
                  <td><NoTranslate>{user.position || '-'}</NoTranslate></td>
                  <td>
                    <div className="user-roles">
                      {Boolean(user.is_admin) && <NoTranslate className="badge badge-danger">Admin</NoTranslate>}
                      {Boolean(user.is_approver) && <NoTranslate className="badge badge-warning">Approver</NoTranslate>}
                      {!Boolean(user.is_admin) && !Boolean(user.is_approver) && <NoTranslate className="badge badge-secondary">User</NoTranslate>}
                    </div>
                  </td>
                  <td>
                    <NoTranslate className={`badge ${Boolean(user.is_active) ? 'badge-success' : 'badge-secondary'}`}>
                      {Boolean(user.is_active) ? 'Active' : 'Inactive'}
                    </NoTranslate>
                  </td>
                  <td>
                    <div className="user-actions">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => openUserModal('edit', user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => handlePasswordReset(user.id, user.employee_id)}
                      >
                        Reset pass
                      </button>
                      {Boolean(user.is_active) && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeactivateUser(user.id, user.employee_id)}
                        >
                          Deactivate
                        </button>
                      )}
                      {!Boolean(user.is_active) && (
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => handleActivateUser(user.id, user.employee_id)}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User creation/editing modal */}
      {userModal.show && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>
              {userModal.mode === 'create' ? 'Create New User' : 'Edit User'}
            </h4>
            
            <form onSubmit={handleUserSubmit}>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Employee ID *</label>
                  <input
                    type="text"
                    name="employee_id"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.employee_id}
                    onChange={handleUserFormChange}
                    required
                    disabled={userModal.mode === 'edit'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.full_name}
                    onChange={handleUserFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.email}
                    onChange={handleUserFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input
                    type="text"
                    name="department"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.department}
                    onChange={handleUserFormChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Position</label>
                  <input
                    type="text"
                    name="position"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.position}
                    onChange={handleUserFormChange}
                  />
                </div>


                <div className="form-group">
                  <label className="form-label">Annual Leave Days</label>
                  <input
                    type="number"
                    name="annual_leave_days"
                    className="form-control"
                    value={userModal.user.annual_leave_days}
                    onChange={handleUserFormChange}
                    min="0"
                    max="60"
                    step="0.5"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Slack User ID</label>
                  <input
                    type="text"
                    name="slack_user_id"
                    className="form-control notranslate"
                    translate="no"
                    value={userModal.user.slack_user_id || ''}
                    onChange={handleUserFormChange}
                    placeholder="U01234567890 (Optional)"
                  />
                  <small className="form-text text-muted">
                    Slack user ID for mentions (e.g., U01234567890)
                  </small>
                </div>
              </div>

              <div className="user-permissions">
                <h5>Permission Settings</h5>
                <div className="permission-checkboxes">
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      name="is_admin"
                      checked={userModal.user.is_admin}
                      onChange={handleUserFormChange}
                    />
                    <span>Admin Permission</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      name="is_approver"
                      checked={userModal.user.is_approver}
                      onChange={handleUserFormChange}
                    />
                    <span>Approver Permission</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={userModal.user.is_active}
                      onChange={handleUserFormChange}
                    />
                    <span>Active User</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeUserModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {userModal.mode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV import modal */}
      {csvModal.show && (
        <div className="modal-overlay" onClick={() => setCsvModal({ show: false, file: null, importing: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>CSV Import</h4>
            
            <div className="csv-instructions">
              <h5>📋 Import Instructions</h5>
              <ol>
                <li>Download CSV template</li>
                <li>Fill in user information in template</li>
                <li>Select file and execute import</li>
              </ol>
              
              <div className="csv-format">
                <h5>📄 CSV Format</h5>
                <p><strong>Column Order:</strong> Employee ID, Name, Email, Department, Position, Leave Days, Admin Flag, Approver Flag, Initial Password</p>
                <p><strong>Permission Flags:</strong> true or false</p>
                <p><strong>Date Format:</strong> YYYY-MM-DD</p>
                <p><strong>Initial Password:</strong> If empty, defaults to Employee ID + "123"</p>
              </div>
            </div>

            <div className="csv-actions">
              <button 
                className="btn btn-outline-secondary"
                onClick={downloadCsvTemplate}
              >
                📥 Download Template
              </button>
              
              <div className="file-input-group">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                  className="form-control"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setCsvModal({ show: false, file: null, importing: false })}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-success"
                onClick={handleCsvImport}
                disabled={!csvModal.file || csvModal.importing}
              >
                {csvModal.importing ? 'Importing...' : 'Execute Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-actions {
          display: flex;
          gap: 0.25rem;
        }
        
        .admin-actions .btn {
          padding-left: 10px;
          padding-right: 10px;
        }
        
        .search-result {
          padding: 0.5rem;
          background-color: #f8f9fa;
          border-radius: 4px;
          font-weight: 500;
          color: #495057;
        }
        
        .inactive-user {
          opacity: 0.6;
        }
        
        .user-roles {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
        }
        
        .user-actions {
          display: flex;
          gap: 0.125rem;
          flex-wrap: wrap;
          min-width: 300px;
        }
        
        .user-actions .btn {
          min-width: 85px;
          white-space: nowrap;
          font-size: 0.875rem;
          padding-left: 10px;
          padding-right: 10px;
        }
        
        .table th:last-child,
        .table td:last-child {
          width: 300px;
          min-width: 300px;
        }
        
        .table-responsive {
          overflow-x: auto;
          min-width: 100%;
        }
        
        .table {
          table-layout: auto;
          width: 100%;
          min-width: 1200px;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          padding: 2rem;
          border-radius: 8px;
          min-width: 600px;
          max-width: 90vw;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .user-permissions {
          margin: 1.5rem 0;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
        
        .permission-checkboxes {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .csv-instructions {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1rem;
        }
        
        .csv-format {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #dee2e6;
        }
        
        .csv-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .file-input-group {
          display: flex;
          align-items: center;
        }
        
        @media (max-width: 768px) {
          .modal-content {
            min-width: auto;
            margin: 1rem;
            padding: 1rem;
          }
          
          .admin-actions {
            flex-direction: column;
          }
          
          .user-actions {
            flex-direction: column;
            min-width: auto;
            width: 100%;
          }
          
          .user-actions .btn {
            min-width: auto;
            width: 100%;
            margin-bottom: 0.25rem;
          }
          
          .table th:last-child,
          .table td:last-child {
            width: auto;
            min-width: 120px;
          }
          
          .permission-checkboxes {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default UserManagement;
